import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

import Icon from '../../../components/AppIcon';

const ProcessReturnModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    challanId: '',
    returnDate: new Date().toISOString().split('T')[0],
    returnReason: '',
    returnType: 'partial',
    notes: ''
  });

  const [scannedItems, setScannedItems] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');

  const availableChallans = [
    { 
      value: 'CH-2025-002', 
      label: 'CH-2025-002 - Fashion Hub (Delivered)', 
      client: 'Fashion Hub Pvt Ltd',
      status: 'delivered',
      items: [
        { barcode: 'GEO001S001', design: 'Geometric Pattern', size: 'S', pieces: 20 },
        { barcode: 'GEO001M001', design: 'Geometric Pattern', size: 'M', pieces: 25 },
        { barcode: 'VIN001L001', design: 'Vintage Style', size: 'L', pieces: 30 },
        { barcode: 'VIN001XL001', design: 'Vintage Style', size: 'XL', pieces: 15 }
      ]
    },
    { 
      value: 'CH-2025-003', 
      label: 'CH-2025-003 - MYNTRA (Returned)', 
      client: 'MYNTRA',
      status: 'returned',
      items: [
        { barcode: 'SUM001S001', design: 'Summer Collection', size: 'S', pieces: 40 },
        { barcode: 'SUM001M001', design: 'Summer Collection', size: 'M', pieces: 50 },
        { barcode: 'CAS001L001', design: 'Casual Wear', size: 'L', pieces: 35 }
      ]
    }
  ];

  const returnReasonOptions = [
    { value: 'quality-issue', label: 'Quality Issue' },
    { value: 'wrong-size', label: 'Wrong Size' },
    { value: 'color-mismatch', label: 'Color Mismatch' },
    { value: 'damaged', label: 'Damaged in Transit' },
    { value: 'client-request', label: 'Client Request' },
    { value: 'overstock', label: 'Overstock Return' }
  ];

  const returnTypeOptions = [
    { value: 'partial', label: 'Partial Return' },
    { value: 'full', label: 'Full Return' }
  ];

  if (!isOpen) return null;

  const selectedChallan = availableChallans.find(ch => ch.value === formData.challanId);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    if (!barcodeInput.trim() || !selectedChallan) return;

    const item = selectedChallan.items.find(item => item.barcode === barcodeInput.trim());
    if (item) {
      const existingItem = scannedItems.find(si => si.barcode === item.barcode);
      if (existingItem) {
        setScannedItems(scannedItems.map(si => 
          si.barcode === item.barcode 
            ? { ...si, returnQuantity: Math.min(si.returnQuantity + 1, si.pieces) }
            : si
        ));
      } else {
        setScannedItems([...scannedItems, { ...item, returnQuantity: 1 }]);
      }
      setBarcodeInput('');
    } else {
      alert('Barcode not found in selected challan');
    }
  };

  const updateReturnQuantity = (barcode, quantity) => {
    setScannedItems(scannedItems.map(item => 
      item.barcode === barcode 
        ? { ...item, returnQuantity: Math.max(0, Math.min(quantity, item.pieces)) }
        : item
    ));
  };

  const removeScannedItem = (barcode) => {
    setScannedItems(scannedItems.filter(item => item.barcode !== barcode));
  };

  const handleSave = () => {
    if (!formData.challanId || !formData.returnReason) {
      alert('Please select a challan and return reason');
      return;
    }

    if (scannedItems.length === 0) {
      alert('Please scan at least one item to return');
      return;
    }

    const returnData = {
      ...formData,
      client: selectedChallan.client,
      returnedItems: scannedItems,
      totalReturnedPieces: scannedItems.reduce((sum, item) => sum + item.returnQuantity, 0)
    };

    onSave(returnData);
    onClose();
    
    // Reset form
    setFormData({
      challanId: '',
      returnDate: new Date().toISOString().split('T')[0],
      returnReason: '',
      returnType: 'partial',
      notes: ''
    });
    setScannedItems([]);
    setBarcodeInput('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-1001 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="RotateCcw" size={24} className="text-warning" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Process Return</h2>
              <p className="text-sm text-muted-foreground">Handle returned items with barcode scanning</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Return Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Return Information</h3>
                <div className="space-y-4">
                  <Select
                    label="Select Challan"
                    placeholder="Choose challan for return"
                    options={availableChallans}
                    value={formData.challanId}
                    onChange={(value) => handleInputChange('challanId', value)}
                    required
                    searchable
                  />

                  {selectedChallan && (
                    <div className="p-3 bg-muted/30 border border-border rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium text-foreground">{selectedChallan.client}</div>
                          <div className="text-sm text-muted-foreground">
                            Status: {selectedChallan.status} • {selectedChallan.items.length} item types
                          </div>
                        </div>
                        <Icon name="Package" size={20} className="text-primary" />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Return Date"
                      type="date"
                      value={formData.returnDate}
                      onChange={(e) => handleInputChange('returnDate', e.target.value)}
                      required
                    />

                    <Select
                      label="Return Type"
                      placeholder="Select return type"
                      options={returnTypeOptions}
                      value={formData.returnType}
                      onChange={(value) => handleInputChange('returnType', value)}
                      required
                    />
                  </div>

                  <Select
                    label="Return Reason"
                    placeholder="Select reason for return"
                    options={returnReasonOptions}
                    value={formData.returnReason}
                    onChange={(value) => handleInputChange('returnReason', value)}
                    required
                  />

                  <Input
                    label="Notes (Optional)"
                    type="text"
                    placeholder="Additional notes about the return"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                  />
                </div>
              </div>

              {/* Barcode Scanner */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Barcode Scanner</h3>
                <form onSubmit={handleBarcodeSubmit} className="space-y-4">
                  <Input
                    label="Scan/Enter Barcode"
                    type="text"
                    placeholder="Scan barcode or enter manually"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    disabled={!selectedChallan}
                    description="Scan the barcode on returned items"
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    disabled={!barcodeInput.trim() || !selectedChallan}
                    iconName="Scan"
                    iconPosition="left"
                    fullWidth
                  >
                    Add Scanned Item
                  </Button>
                </form>

                {selectedChallan && (
                  <div className="mt-4">
                    <h4 className="font-medium text-foreground mb-2">Available Items in Challan:</h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {selectedChallan.items.map((item) => (
                        <div key={item.barcode} className="text-xs p-2 bg-muted/30 rounded border">
                          <div className="font-mono">{item.barcode}</div>
                          <div className="text-muted-foreground">
                            {item.design} - {item.size} ({item.pieces} pcs)
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Scanned Items */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Scanned Return Items</h3>
                {scannedItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="ScanLine" size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No items scanned</p>
                    <p className="text-sm">Scan barcodes to add return items</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {scannedItems.map((item) => (
                      <div key={item.barcode} className="border border-border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-medium text-foreground">{item.design}</div>
                            <div className="text-sm text-muted-foreground">
                              Size: {item.size} • Available: {item.pieces} pcs
                            </div>
                            <div className="text-xs font-mono text-muted-foreground mt-1">
                              {item.barcode}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeScannedItem(item.barcode)}
                            iconName="Trash2"
                            iconSize={14}
                            className="text-error hover:text-error"
                          />
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-muted-foreground">Return Qty:</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateReturnQuantity(item.barcode, item.returnQuantity - 1)}
                            iconName="Minus"
                            iconSize={12}
                          />
                          <span className="w-12 text-center font-medium">{item.returnQuantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateReturnQuantity(item.barcode, item.returnQuantity + 1)}
                            iconName="Plus"
                            iconSize={12}
                          />
                          <span className="text-sm text-muted-foreground">/ {item.pieces}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Return Summary */}
              {scannedItems.length > 0 && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <h4 className="font-semibold text-foreground mb-3">Return Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total Items:</span>
                      <span className="font-medium">{scannedItems.length} types</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Pieces:</span>
                      <span className="font-medium">
                        {scannedItems.reduce((sum, item) => sum + item.returnQuantity, 0)} pcs
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Return Reason:</span>
                      <span className="font-medium capitalize">
                        {formData.returnReason.replace('-', ' ')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Return Type:</span>
                      <span className="font-medium capitalize">{formData.returnType}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={handleSave}
            disabled={!formData.challanId || !formData.returnReason || scannedItems.length === 0}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Process Return
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProcessReturnModal;